import React, { useContext } from "react";
import Page from "../../page-layout";
import MaxWidthContainer from "../../../components/UI/MaxWidthContainer/MaxWidthContainer";
import { BackButton } from "../../../parhato2/src/entities";
import { IonIcon, IonSkeletonText, IonToggle, useIonAlert } from "@ionic/react";
import { useAuth } from "../../../contexts/auth-context";
import { useGetProfileQuery } from "../../../providers/profile";
import { useHistory } from "react-router";
import { AppContext } from "../../../providers/Redux/Reducers/AppContext";
import { useConfig } from "../../../providers/config";
import { formatPhoneNumber } from "../../../components/CustomPhoneInput/phoneUtils";
import { callOutline, personOutline, pencilOutline, mailOutline } from "ionicons/icons";
import UserProfileEditor from "../../../components/UserProfileEditor/UserProfileEditor";
import cln from "classnames";
import ToggleActiveModule from "../../../components/ToggleActiveModule";
import { useTranslation } from "react-i18next";

const Divider = () => <hr className="h-[1px] w-full my-3 bg-gray-300" />;

const SettingSection = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <>
    <div className="flex mt-2">
      <h1 className="font-bold">{title}</h1>
    </div>
    <Divider />
    {children}
  </>
);

const NotificationToggle = ({ label }: { label: string }) => (
  <>
    <div className="flex items-center justify-between">
      <span className="font-thin">{label}</span>
      <IonToggle color={"success"} />
    </div>
    <Divider />
  </>
);

const SettingsPage = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const config = useConfig();
  const { user, logout } = useAuth();
  const [presentAlert] = useIonAlert();
  const { dispatch } = useContext(AppContext);

  const { data: fetchedProfile, isLoading: isLoadingProfile } = useGetProfileQuery({
    userId: user?.id || "",
    options: {
      enabled: !!user?.id,
    },
  });

  const parsedPhone = fetchedProfile?.phone ? formatPhoneNumber(fetchedProfile.phone) : "";

  const handleLogout = () => {
    presentAlert({
      header: t("Confirm Logout"),
      message: t("Are you sure you want to log out?"),
      buttons: [
        {
          text: t("Cancel"),
          role: "cancel",
        },
        {
          text: t("Confirm"),
          role: "confirm",
          handler: () => {
            history.replace("/auth");
            history.go(-1);
            dispatch({
              type: "REMOVE_CUSTOMER_DATA",
            });
            logout();
          },
        },
      ],
    });
  };

  const handleDeleteAccount = () => {
    presentAlert(t("Delete account description"), [
      {
        text: "Нет",
        role: "cancel",
      },
      {
        text: "Да",
        handler: async () => {
          window.open(config?.deleteAccountLink);
        },
      },
    ]);
  };

  return (
    <Page>
      <Page.Header className="flex items-center justify-start h-16 mt-[env(safe-area-inset-top)]">
        <MaxWidthContainer mdContainer={true} classList="flex items-center justify-center bg-white py-2.5 ">
          <div className="flex justify-justify-between items-center w-full pl-2.5 relative">
            <div className="absolute left-2.5">
              <BackButton />
            </div>
            <div className="flex-1 text-center font-bold text-[1.3rem] w-[70%]">{t("Settings")}</div>
            {config?.isToggleSwitchModuleEnabled && (
              <div className="absolute right-0">
                <ToggleActiveModule isViewDescription={false} isViewPopover containerClassName="pr-2.5" />
              </div>
            )}
          </div>
        </MaxWidthContainer>
      </Page.Header>

      <Page.Content>
        <MaxWidthContainer mdContainer={true}  classList="flex flex-col gap-5">
          <div className="flex flex-col mx-4">
            <SettingSection title={t("Profile")}>
              <div className="flex items-center justify-between gap-1">
                <div className={cln("flex items-center gap-2", { "opacity-80": isLoadingProfile })}>
                  <IonIcon icon={callOutline} color="gray" />
                  {isLoadingProfile ? (
                    <IonSkeletonText animated className="h-4 w-32 rounded-sm m-0" />
                  ) : (
                    <h1>{parsedPhone}</h1>
                  )}
                </div>
              </div>
              <Divider />

              <UserProfileEditor
                field={{
                  name: "firstName",
                  label: "Имя",
                  initialValue: fetchedProfile?.firstName || "",
                  type: "text",
                  required: true,
                }}
                disabled={isLoadingProfile}
                previewContent={
                  <>
                    <div className="flex items-center justify-between gap-1">
                      <div className={cln("flex items-center gap-2", { "opacity-80": isLoadingProfile })}>
                        <IonIcon icon={personOutline} />
                        {isLoadingProfile ? (
                          <IonSkeletonText animated className="h-4 w-32 rounded-md m-0" />
                        ) : (
                          <h1 className={cln(!fetchedProfile?.firstName?.length && "text-gray-400")}>
                            {fetchedProfile?.firstName || "Нажмите чтобы изменить имя"}
                          </h1>
                        )}
                      </div>
                      <IonIcon icon={pencilOutline} color="gray" />
                    </div>
                    <Divider />
                  </>
                }
              />

              {config.isEmailPushEnabled && (
                <UserProfileEditor
                  field={{
                    name: "email",
                    label: "Email",
                    initialValue: user?.email || "",
                    type: "email",
                    required: true,
                    rules: {
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: "Invalid email address",
                      },
                    },
                  }}
                  previewContent={
                    <>
                      <div className="flex items-center justify-between gap-1">
                        <div className="flex items-center gap-2">
                          <IonIcon icon={mailOutline} />
                          <h1>{user?.email}</h1>
                        </div>
                        <IonIcon icon={pencilOutline} color="gray" />
                      </div>
                      <Divider />
                    </>
                  }
                />
              )}
            </SettingSection>

            {config.notifications?.isOrderNotificationsEnabled && (
              <SettingSection title={t("Order notifications")}>
                {config.notifications?.orderNotifications?.isPushEnabled && (
                  <NotificationToggle label={t("Push notifications")} />
                )}
              </SettingSection>
            )}

            {config.notifications?.isDiscountNotificationsEnabled && (
              <SettingSection title={t("Discount notifications")}>
                {config.notifications?.discountNotifications?.isPushEnabled && (
                  <NotificationToggle label={t("Push notifications")} />
                )}
                {config.notifications?.discountNotifications?.isEmailEnabled && (
                  <NotificationToggle label={t("Email notifications")} />
                )}
                {config.notifications?.discountNotifications?.isSmsEnabled && (
                  <NotificationToggle label={t("SMS notifications")} />
                )}
              </SettingSection>
            )}

            <div className="flex flex-col mx-5 gap-2 mt-5">
              <button className="rounded-3xl h-12 bg-primary" onClick={handleLogout}>
                {t("Log Out")}
              </button>
              <button className="rounded-3xl h-12 text-red-600" onClick={handleDeleteAccount}>
                {t("Delete account")}
              </button>
            </div>
          </div>
        </MaxWidthContainer>
      </Page.Content>
    </Page>
  );
};

export default SettingsPage;
